import type { RouteRecordRaw, NavigationGuardWithThis } from 'vue-router'
import { useFeaturesStore } from '@/v2/store/features'

// Gates the whole Help Center behind the "profile.help" flag. When the flag is
// off (or fails to resolve) the page is treated as non-existent — the user is
// routed to the NotFound (404) view while keeping the attempted URL. Applied to
// the parent route, so it covers every help tab.
const requireHelpEnabled: NavigationGuardWithThis<undefined> = async (to) => {
  const featuresStore = useFeaturesStore()
  try {
    await featuresStore.load()
    if (featuresStore.isHelpPageEnabled) return true
  } catch {
    // fall through to the 404 redirect
  }
  return {
    name: 'NotFound',
    params: { pathMatch: to.path.split('/').filter(Boolean) },
    query: to.query,
    hash: to.hash
  }
}

// Help Center — a shell page (title + tab bar) whose child routes render each
// tab's content. The active tab is derived from the route path. Candidate-
// facing, behind auth.
const helpRoutes: RouteRecordRaw[] = [
  {
    path: 'help',
    component: () => import('@/v2/pages/help/HelpPage.vue'),
    beforeEnter: requireHelpEnabled,
    meta: {
      layout: 'default',
      requiresAuth: true,
      background: 'secondary'
    },
    children: [
      {
        path: '',
        name: 'help',
        component: () => import('@/v2/pages/help/tabs/HelpPlatformUsageTab.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'faq',
        name: 'helpFaq',
        component: () => import('@/v2/pages/help/tabs/HelpFaqTab.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'data-protection',
        name: 'helpDataProtection',
        component: () => import('@/v2/pages/help/tabs/HelpDataProtectionTab.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'about-rocken',
        name: 'helpAboutRocken',
        component: () => import('@/v2/pages/help/tabs/HelpAboutRockenTab.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'contact-support',
        name: 'helpContactSupport',
        component: () => import('@/v2/pages/help/tabs/HelpContactSupportTab.vue'),
        meta: { requiresAuth: true }
      }
    ]
  }
]

export default helpRoutes
